import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/shared/libs/axiosClient";
import { queryKeys } from "@/shared/libs/queryClient";
import { toast } from "@/shared/hooks/useToast";
import type { FacebookResourceTargetDateRow } from "@/types/preferences_types";

// ============================================
// API 응답/요청 타입
// ============================================

interface FacebookResourceTargetDateState {
  defaultBackwardDays: number;
  rows: FacebookResourceTargetDateRow[];
}

interface UpdateDefaultBackwardDaysRequest {
  defaultBackwardDays: number;
}

interface UpsertTargetDateRequest {
  adAccountId: string;
  targetDate: string;
}

// ============================================
// API 함수들
// ============================================

/**
 * 페이스북 리소스 target_date 전체 조회 (default + rows)
 */
async function fetchFacebookResourceTargetDates(): Promise<FacebookResourceTargetDateState> {
  return api.get<FacebookResourceTargetDateState>("/facebook-resource-target-dates/");
}

/**
 * 기본 과거 데이터 조회일 Window 수정
 */
async function updateDefaultBackwardDays(defaultBackwardDays: number): Promise<void> {
  const request: UpdateDefaultBackwardDaysRequest = { defaultBackwardDays };
  return api.put<void>("/facebook-resource-target-dates/default", request);
}

/**
 * target_date 추가/수정 (upsert)
 */
async function upsertTargetDate(adAccountId: string, targetDate: string): Promise<void> {
  const request: UpsertTargetDateRequest = { adAccountId, targetDate };
  return api.post<void>("/facebook-resource-target-dates/", request);
}

/**
 * target_date 삭제 (여러 개)
 */
async function deleteTargetDates(adAccountIds: string[]): Promise<void> {
  return api.delete<void>("/facebook-resource-target-dates/", {
    data: adAccountIds,
  });
}

// ============================================
// Query 훅들
// ============================================

/**
 * 페이스북 리소스 target_date 전체 상태 조회 Query
 */
export function useFacebookResourceTargetDatesQuery() {
  return useQuery({
    queryKey: queryKeys.facebookResourceTargetDates.state(),
    queryFn: fetchFacebookResourceTargetDates,
  });
}

// ============================================
// Mutation 훅들
// ============================================

/**
 * 기본 과거 데이터 조회일 Window 수정 Mutation
 *
 * Note: onSuccess에서 자동으로 invalidate하지 않습니다.
 * 페이지에서 수동으로 refetch해야 합니다.
 */
export function useUpdateDefaultBackwardDaysMutation() {
  return useMutation({
    mutationFn: updateDefaultBackwardDays,
    onError: (error: Error) => {
      toast({
        title: "저장 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * target_date 추가/수정 Mutation
 *
 * Note: onSuccess에서 자동으로 invalidate하지 않습니다.
 * 다이얼로그를 먼저 닫은 후 페이지에서 수동으로 refetch해야 합니다.
 */
export function useUpsertTargetDateMutation() {
  return useMutation({
    mutationFn: ({ adAccountId, targetDate }: { adAccountId: string; targetDate: string }) =>
      upsertTargetDate(adAccountId, targetDate),
    onError: (error: Error) => {
      toast({
        title: "저장 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * target_date 삭제 Mutation
 *
 * Note: onSuccess에서 자동으로 invalidate하지 않습니다.
 * 다이얼로그를 먼저 닫은 후 페이지에서 수동으로 refetch해야 합니다.
 */
export function useDeleteTargetDatesMutation() {
  return useMutation({
    mutationFn: deleteTargetDates,
    onError: (error: Error) => {
      toast({
        title: "삭제 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
